import { registerPlugin, WebPlugin } from '@capacitor/core';
import localforage from 'localforage';

export interface ConfiguracionAlmacenamientoOptions {
  nombre: string;
  webStoreName?: string;
}

export interface DatosPersistentesPlugin {
  configurarAlmacenamiento(options: ConfiguracionAlmacenamientoOptions): void;
  escribir(options: EscribirOptions): Promise<void>;
  leer(options: LeerOptions): Promise<LeerResult>;
  borrar(options: BorrarOptions): Promise<void>;
  vaciar(): Promise<void>;
}

export interface EscribirOptions {
  clave: string;
  valor: string;
}
export interface LeerOptions {
  clave: string;
}
export interface LeerResult {
  valor?: string;
}
export interface BorrarOptions {
  clave: string;
}

// * Prefijos que identifican la capa en nativo (Android/iOS ruteará por ellos)
const PREFIJO_SEGURO = 'seguro_'
const PREFIJO_PERSONAL = 'personal_'

class DatosPersistentesWeb extends WebPlugin implements DatosPersistentesPlugin {
  private seguroInstance: LocalForage;
  private personalInstance: LocalForage;
  private legacyInstance: LocalForage;

  constructor() {
    super();
    this.seguroInstance = localforage.createInstance({ name: 'dp_seguro' })
    this.personalInstance = localforage.createInstance({ name: 'dp_personal' })
    this.legacyInstance = localforage.createInstance({ name: 'defaultDB', storeName: 'defaultStore' })
  }

  private resolverInstancia(clave: string): { instancia: LocalForage, claveLocal: string } {
    if (clave.startsWith(PREFIJO_SEGURO))
      return { instancia: this.seguroInstance, claveLocal: clave.slice(PREFIJO_SEGURO.length) }
    if (clave.startsWith(PREFIJO_PERSONAL))
      return { instancia: this.personalInstance, claveLocal: clave.slice(PREFIJO_PERSONAL.length) }
    return { instancia: this.legacyInstance, claveLocal: clave }
  }

  async configurarAlmacenamiento(_options: ConfiguracionAlmacenamientoOptions): Promise<void> {}

  async escribir(options: EscribirOptions): Promise<void> {
    const { instancia, claveLocal } = this.resolverInstancia(options.clave)
    await instancia.setItem(claveLocal, options.valor);
  }

  async leer(options: LeerOptions): Promise<LeerResult> {
    const { instancia, claveLocal } = this.resolverInstancia(options.clave)
    const valor = await instancia.getItem<string>(claveLocal);
    return { valor: valor || undefined };
  }

  async borrar(options: BorrarOptions): Promise<void> {
    const { instancia, claveLocal } = this.resolverInstancia(options.clave)
    await instancia.removeItem(claveLocal);
  }

  async vaciar(): Promise<void> {
    await Promise.all([
      this.seguroInstance.clear(),
      this.personalInstance.clear(),
      this.legacyInstance.clear()
    ])
  }
}

export const DatosPersistentesMultiplataforma = registerPlugin<DatosPersistentesPlugin>('DatosPersistentes', {
  web: () => new DatosPersistentesWeb()
});

// * API base compartida

export type DatosPersistentesAPI = {
  guardar: (clave: string, valor: string) => Promise<void>
  leer: (clave: string) => Promise<string | null>
  borrar: (clave: string) => Promise<void>
  vaciar: () => Promise<void>
}

function crearPersistencia(prefijo: string): DatosPersistentesAPI {
  return {
    async guardar(clave: string, valor: string) {
      try {
        await DatosPersistentesMultiplataforma.escribir({ clave: prefijo + clave, valor })
      } catch (e) {
        consolo.info(`No se pudo guardar el contenido en "${clave}"`, e)
      }
    },
    async leer(clave: string): Promise<string | null> {
      try {
        const { valor } = await DatosPersistentesMultiplataforma.leer({ clave: prefijo + clave })
        return valor ?? null
      } catch (e) {
        consolo.info(`No se pudo leer el contenido de "${clave}"`, e)
        return null
      }
    },
    async borrar(clave: string) {
      try {
        await DatosPersistentesMultiplataforma.borrar({ clave: prefijo + clave })
      } catch (e) {
        consolo.info(`No se pudo borrar el contenido de "${clave}"`, e)
      }
    },
    async vaciar() {
      try {
        await DatosPersistentesMultiplataforma.vaciar()
      } catch (e) {
        consolo.info('No se pudo vaciar el almacenamiento', e)
      }
    }
  }
}

// * Capa 1: Seguro — no respaldable, identidad de dispositivo (llavero, registroID)
export const DatosPersistentesSeguro: DatosPersistentesAPI = crearPersistencia(PREFIJO_SEGURO)

// * Capa 2: Personal — respaldable, identidad de usuario (token, miniUsuario)
export const DatosPersistentesPersonal: DatosPersistentesAPI = {
  ...crearPersistencia(PREFIJO_PERSONAL),
  async vaciar() {
    try {
      await Promise.all([
        DatosPersistentesMultiplataforma.borrar({ clave: PREFIJO_PERSONAL + 'token' }),
        DatosPersistentesMultiplataforma.borrar({ clave: PREFIJO_PERSONAL + 'miniUsuario' })
      ])
    } catch (e) {
      consolo.info('No se pudo vaciar almacenamiento personal', e)
    }
  }
}

// * Capa 3: Caché — localforage puro, datos de última sesión, preferencias
export const AlmacenamientoTemporal = {
  _instancia: localforage.createInstance({ name: 'cache_app' }),
  async guardar<T>(clave: string, valor: T): Promise<void> {
    try {
      await AlmacenamientoTemporal._instancia.setItem(clave, valor)
    } catch (e) {
      consolo.info(`AlmacenamientoTemporal: no se pudo guardar "${clave}"`, e)
    }
  },
  async leer<T>(clave: string): Promise<T | null> {
    try {
      return await AlmacenamientoTemporal._instancia.getItem<T>(clave)
    } catch (e) {
      consolo.info(`AlmacenamientoTemporal: no se pudo leer "${clave}"`, e)
      return null
    }
  },
  async borrar(clave: string): Promise<void> {
    try {
      await AlmacenamientoTemporal._instancia.removeItem(clave)
    } catch (e) {
      consolo.info(`AlmacenamientoTemporal: no se pudo borrar "${clave}"`, e)
    }
  },
  async vaciar(): Promise<void> {
    try {
      await AlmacenamientoTemporal._instancia.clear()
    } catch (e) {
      consolo.info('AlmacenamientoTemporal: no se pudo vaciar', e)
    }
  }
}

// * Compatibilidad: DatosPersistentes sin prefijo → instancia legacy (migración gradual)
export const DatosPersistentes: DatosPersistentesAPI = crearPersistencia('')

// * Migración one-shot: mueve claves legacy a su capa correcta
// Clave legacy → clave nueva (con prefijo de capa)
const MAPA_MIGRACION: Record<string, string> = {
  'llavero_llaveroCliente':  PREFIJO_SEGURO  + 'llavero_llaveroCliente',
  'llavero_llaveroServidor': PREFIJO_SEGURO  + 'llavero_llaveroServidor',
  'llavero_registroID':      PREFIJO_SEGURO  + 'llavero_registroID',
  'token':                   PREFIJO_PERSONAL + 'token'
}

export async function migrarAlmacenamientoLegacy(): Promise<string[]> {
  const fx = 'migrarAlmacenamientoLegacy'
  const migradas: string[] = []
  for (const [claveOrigen, claveDestino] of Object.entries(MAPA_MIGRACION)) {
    try {
      const valorExistente = await DatosPersistentesMultiplataforma.leer({ clave: claveDestino })
      if (valorExistente.valor) continue

      const valorLegacy = await DatosPersistentesMultiplataforma.leer({ clave: claveOrigen })
      if (!valorLegacy.valor) continue

      await DatosPersistentesMultiplataforma.escribir({ clave: claveDestino, valor: valorLegacy.valor })
      migradas.push(claveOrigen)
      consolo.log(`${fx}: migrado "${claveOrigen}" → "${claveDestino}"`)
    } catch (e) {
      consolo.info(`${fx}: error migrando "${claveOrigen}"`, e)
    }
  }
  if (migradas.length > 0) consolo.log(`${fx}: ${migradas.length} claves migradas, pendientes de limpieza tras verificación`)
  return migradas
}

export async function limpiarLegacyMigrado(clavesMigradas: string[]): Promise<void> {
  const fx = 'limpiarLegacyMigrado'
  for (const clave of clavesMigradas) {
    try {
      await DatosPersistentesMultiplataforma.borrar({ clave })
      consolo.log(`${fx}: eliminado legacy "${clave}"`)
    } catch (e) {
      consolo.info(`${fx}: error eliminando legacy "${clave}"`, e)
    }
  }
}
